import { Component, Input, OnInit, NgZone, HostListener, AfterViewInit, ViewChild, ElementRef, ViewChildren, QueryList } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';

@Component({
    selector: 'cv-calculator',
    templateUrl: './calculator.component.html',
    styleUrls: ['./calculator.component.scss']
})
export class CalculatorComponent implements OnInit, AfterViewInit {
    /* Calculator code from https://github.com/nahkar/js-calculator
            with some modifications */

    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    @ViewChild('input', { read: ElementRef }) inputEl: ElementRef<HTMLInputElement>;
    @ViewChild('inputResult', { read: ElementRef }) inputResultEl: ElementRef<HTMLInputElement>;
    @ViewChildren('keyPress') keypressEls: QueryList<ElementRef<HTMLElement>>;

    inputHistory: string = '';
    inputResult: string = '0';

    equalPressed: boolean = true;
    opPressed: boolean = false;
    keypressMap = new Map<string, ElementRef<HTMLElement>>();

    constructor(
        private cvLocalizationService: CVLocalizationService,
        private zone: NgZone,
    ) { }
    
    moduleFocused: boolean = false; 
    
    divideZeroError: boolean = false;
    divideZeroMessage: string = "Cannot divide by zero";
    
    ngOnInit(): void {
        this.cvLocalizationService.GetSection("Calculator").then(() => this.setLocalizations());
        this.moduleManager.registerEvents({
            onGainedFocus: () => {
                this.moduleFocused = true;
            },
            onLostFocus: () => {
                this.moduleFocused = false;
            }
        });

    }

    ngAfterViewInit(): void {
        this.keypressEls.forEach(e => this.keypressMap.set(e.nativeElement.getAttribute('data-keypress'), e));
    }

    setLocalizations(): void {
        var localizedMessage = this.cvLocalizationService.getCachedText("Calculator", "Divide_Zero_Message");
        this.divideZeroMessage = localizedMessage || this.divideZeroMessage;
    }

    @HostListener('document:keyup', ['$event'])
    onKeyUp(ev: KeyboardEvent) {
        if (!this.moduleFocused) {
            return;
        }

        if (this.moduleManager.isModulePaused()) {
            return;
        }

        // IE11 key names are different????
        var key: string = ev.key;
        if (key === "Multiply") {
            key = "*";
        } else if (key === "Divide") {
            key = "/";
        } else if (key === "Subtract") {
            key = "-";
        } else if (key === "Add") {
            key = "+";
        }
        ev.preventDefault();
        this.keypressMap.get(ev.key)?.nativeElement?.click();
    };

    onClick(ev: Event): void {
        ev.preventDefault();
        var element = ev.target as HTMLElement;
        // the value is in the span, but the click might register on it or its parent div
        var value: string = element.childElementCount === 1 
            ? element.firstChild.textContent : element.textContent;
        if (this._isEqually(value)) {
            this.inputEl.nativeElement.value = this.inputEl.nativeElement.value + ' ' + this.inputResultEl.nativeElement.value;
            this.inputResultEl.nativeElement.value = this._getEquall();
            this.inputEl.nativeElement.value = '';
            this.equalPressed = true;
            this.opPressed = false;
        } else if (this._isReset(value)) {
            this._reset();
        } else if (this._isRemove(value)) {
            this._removeSymbol();
        } else {
            this._draw(value);
        }
        this.inputEl.nativeElement.scrollLeft = this.inputEl.nativeElement.scrollWidth;
    }

    _reset = () => {
        this.inputEl.nativeElement.value = '';
        this.inputResultEl.nativeElement.value = '0';
        this.opPressed = false;
        this.equalPressed = false;
    };

    _isReset = (val: string) => {
        return !!~val.indexOf('C');
    };

    _isNumber = (val: string) => {
        return !isNaN(Number(val));
    };

    _isDot = (val: string) => {
        return !!~val.indexOf('.');
    };

    _isOperator = (val: string) => {
        var _operators = ['+', '-', 'x', '÷'];
        return !!~_operators.join('').indexOf(val);
    };

    _isEqually = (val: string) => {
        return !!~val.indexOf('=');
    };

    _getLastSymbol = (val: string) => {
        if (val.length > 0) {
            return val[val.length - 1];
        } else {
            return '0';
        }
    };

    _replacesOperator = (val: string) => {
        return val.replace(/x/g, '*').replace(/÷/g, '/');
    };

    _isRemove = (val: string) => {
        return !!~'←'.indexOf(val);
    };

    _removeSymbol = () => {
        const val = this.inputResultEl.nativeElement.value;
        if (val && val !== '0') {
            this.inputResultEl.nativeElement.value = val.substr(0, val.length - 1);
        }
        if (!this.inputResultEl.nativeElement.value.length) {
            this.inputResultEl.nativeElement.value = '0';
        }
    };

    _draw = (val) => {
        const inputVal = this.inputEl.nativeElement.value;
        const resultVal = this.inputResultEl.nativeElement.value;
        var lastSymbol: string = this._getLastSymbol(resultVal);
        var lastInput: string = this._getLastSymbol(inputVal);
        var lastIsDot: boolean = this._isDot(lastSymbol);
        var hasDot: boolean = this._isDot(resultVal);

        var isOperator: boolean = this._isOperator(val);
        var isDot: boolean = this._isDot(val);
        var isNumber: boolean = this._isNumber(val);
        var hasDivideZeroResult: boolean = resultVal === this.divideZeroMessage;

        var isZero: boolean = +resultVal === 0 && !this.opPressed && inputVal === '';

        if (hasDot && isDot && !this.equalPressed && !this.opPressed) {
            // already has dot
            return;
        }

        if (!isOperator && ((isZero && !isDot && !lastIsDot && !hasDot) || hasDivideZeroResult)) {
            // number pushed while input is zero or error
            this.inputResultEl.nativeElement.value = val;
            if (hasDivideZeroResult) {
                this.inputEl.nativeElement.value = '';
            }
        } else if (isZero && hasDot) {
            // case where input is technically zero but trying to do hundredths, thousandths, etc
            this.inputResultEl.nativeElement.value = resultVal + val;
        } else if (isZero && isDot) {
            // . pushed while input is zero
            this.inputResultEl.nativeElement.value = '0' + val;
        } else if (lastIsDot && !isOperator) {
            this.inputResultEl.nativeElement.value = resultVal + val;
        } else if (resultVal && !isZero) {
            if (isNumber || isDot) {
                if (this.opPressed || this.equalPressed) {
                    if (isDot) {
                        val = "0" + val;
                    }
                    this.inputResultEl.nativeElement.value = val;
                    this.opPressed = false;
                    this.equalPressed = false;
                } else {
                    this.inputResultEl.nativeElement.value = resultVal + val;
                }
            } else if (!this.opPressed && isOperator) {
                if (this.divideZeroError) {
                    this.inputResultEl.nativeElement.value = this.divideZeroMessage;
                    this.inputEl.nativeElement.value += ' 0';
                    hasDivideZeroResult = true;
                } else {
                    this.inputEl.nativeElement.value = inputVal + ' ' + resultVal;
                    this.inputResultEl.nativeElement.value = this._getEquall();
                    this.inputEl.nativeElement.value += ' ' + val;
                    this.opPressed = true;
                }
            } else if (this.opPressed && isOperator) {
                this.inputEl.nativeElement.value = inputVal.substr(0, inputVal.length - 1) + val;
            }
        } else if (!this.opPressed && isOperator && isZero) {
            this.inputEl.nativeElement.value = '0 ' + val;
            this.opPressed = true;
        }

        // check for divide by zero
        var lastWasDivide: boolean = lastInput === '÷' || lastInput === '/';
        var zeroPressed: boolean = +val === 0;
        var doingOperationOnError: boolean = isOperator && ((+resultVal === 0 && this.divideZeroError) || hasDivideZeroResult);
        if ((lastWasDivide && zeroPressed) || doingOperationOnError) {
            this.divideZeroError = true;
        } else {
            this.divideZeroError = false;
        }

        this.equalPressed = false;
    };

    _eval = (e) => {
        return Function('"use strict";return (' + e + ')')();
    };

    _getEquall = function () {
        if (this.divideZeroError) {
            return this.divideZeroMessage;
        }

        // eval without order of operations
        const evalString = this.inputEl.nativeElement.value;
        var lastResult: number = 0;
        var lastOp: string = '+';
        var splitVals: string[] = evalString.split(' ');
        for (var i = 0; i < splitVals.length; i++) {
            var val = splitVals[i];
            if (val == "") {
                continue;
            } else if (this._isNumber(val) || val == "Infinity" || val == "NaN") {
                // fix for octal - remove leading 0
                while (val.length > 1 && val.indexOf('0') === 0) {
                    val = val.replace('0', '');
                }

                lastResult = this._eval(this._replacesOperator(lastResult + lastOp + val));
            } else if (this._isOperator(val)) {
                lastOp = val;
            }
        }
        return lastResult;
    };
}
